import {
  BoardFormData,
  BooleanOption,
  getDefaultDisplayTime,
  isTitleShownInList,
} from '@/hooks/board/useBoardForm';
import { CategoryType, MyInfo } from '@/types/api';
import {
  BoardCommentSection,
  BoardDetailData,
  BoardViewType,
} from '@/types/board';
import {
  extendFileToRemoteFile,
  remoteFileToExtendFile,
} from '@/components/file-thumbnail/utils';
import { BoardPost, useGetFileLink } from '@/hooks/apiHooks';
import { getDateFormat } from '@/utils/format';

const dateToPayloadDate = (date?: Date | null) =>
  getDateFormat(date ?? '') || undefined;

const getCategories = (
  categories: string[],
  fullCategoryList: CategoryType[],
) =>
  categories
    .map((c) => fullCategoryList.find((n) => n.id === c))
    .filter((item): item is CategoryType => item !== undefined) // undefined 필터링 및 타입 좁히기
    .map(({ id, name }) => ({ id, name }));

export const boardFormDataToBoardDetailPreview = (
  data: BoardFormData,
  fullCategoryList: CategoryType[],
  userInfo: MyInfo,
  defaultDisplayStartTime: Date | null,
): BoardDetailData => {
  const now = new Date();
  const isReserved = data.useUploadTime === BooleanOption.TRUE;

  const isDisplaySectionNone = data.displaySections === 'none';
  const isSandbox = data.formType === 'codeUpload';

  return {
    id: data.id ?? '',
    commentGroupId: '',
    categories: getCategories(data.categories, fullCategoryList),
    subject: data.subject,
    content: data.content,
    readersCount: 0,
    watchCount: 0,
    createdBy: {
      ...userInfo,
      nickName: userInfo.name,
    },
    createdAt: getDateFormat(data.createdTime ?? now),
    updatedAt: getDateFormat(data.updatedTime ?? now),
    feedback: {
      isRecommend: false,
      isLike: false,
      isBookmark: false,
      likesCount: 0,
      likes: [],
      recommendsCount: 0,
      recommends: [],
    },
    config: {
      isCommentEnable: true,
      isDeleteEnable: false,
      commentSection: data.commentPosition as BoardCommentSection,
      feedbackSettings: { like: true, recommend: true, bookmark: true },
      viewType: data.useImageSlides
        ? BoardViewType.SLIDE
        : (BoardViewType.NORMAL as BoardViewType),
      isTemporary: false,
      isImportant: data.isImportant === BooleanOption.TRUE,
      isSandbox,
      editorType: 'Lexical',
      displaySections: isDisplaySectionNone ? [] : [data.displaySections],
      displayStartedAt: isReserved
        ? getDateFormat(data.displayTime ?? '')
        : getDateFormat(defaultDisplayStartTime ?? ''),
      isReserved,
    },
    files: {
      attachments: extendFileToRemoteFile(data.attachments),
      slideImages: data.useImageSlides
        ? extendFileToRemoteFile(data.imageSlides)
        : [],
      thumbnails:
        isReserved && data.thumbnailFile
          ? extendFileToRemoteFile([data.thumbnailFile])
          : [],
    },
  };
};

export const boardFormDataToPayload = (
  data: BoardFormData,
  isTemporary: boolean,
  uploadDefaultTime: Date | null,
) => {
  const getDisplaySectionOptions = (useThumbnailDisplayTime: boolean) => {
    const thumbnails = data.thumbnailFile
      ? extendFileToRemoteFile([data.thumbnailFile])
      : [];
    const homeDisplayStartedAt = useThumbnailDisplayTime
      ? dateToPayloadDate(data.thumbnailDisplayStartedAt)
      : undefined;
    const homeDisplayEndedAt = useThumbnailDisplayTime
      ? dateToPayloadDate(data.thumbnailDisplayEndAt)
      : undefined;

    return {
      thumbnails,
      homeDisplayStartedAt:
        homeDisplayStartedAt ?? dateToPayloadDate(data.createdTime),
      homeDisplayEndedAt,
    };
  };

  const { thumbnails, homeDisplayStartedAt, homeDisplayEndedAt } =
    getDisplaySectionOptions(
      data.useThumbnailDisplayTime === BooleanOption.TRUE,
    );

  const isReserved = data.useUploadTime === BooleanOption.TRUE;

  const isSandbox = data.formType === 'codeUpload';

  const payload = {
    subject: data.subject,
    content: data.content,
    files: {
      attachments: extendFileToRemoteFile(data.attachments),
      thumbnails: thumbnails,
      slideImages: data.useImageSlides
        ? extendFileToRemoteFile(data.imageSlides)
        : [],
    },
    categories: data.categories,
    config: {
      isTemporary,
      isSandbox,
      viewType: (data.imageSlides.length > 0
        ? 'slide'
        : 'normal') as BoardPost['config']['viewType'],
      displaySections:
        data.displaySections === 'none' ? [] : [data.displaySections],
      displayStartedAt: isReserved
        ? dateToPayloadDate(data.displayTime)
        : dateToPayloadDate(uploadDefaultTime),
      commentSection: data.commentPosition,
      isImportant: data.isImportant === BooleanOption.TRUE,
      editorType: 'LexicalEditor' as BoardPost['config']['editorType'],
      homeDisplayStartedAt,
      homeDisplayEndedAt,
      isReserved,
      isListSubjectVisible: isTitleShownInList(data),
    },
    ...(isSandbox &&
      data.axLoungeDeploy.fileUuid && {
        axLoungeDeploy: data.axLoungeDeploy,
      }),
  };

  return payload;
};

export const boardDetailDataToBoardFormData = async (
  isTemporary: boolean,
  boardDetailData?: BoardDetailData,
  fullCategoryList?: CategoryType[],
): Promise<BoardFormData | undefined> => {
  const getCategories = (
    selectedCategories: BoardDetailData['categories'],
    fullCategoryList: CategoryType[],
  ) => {
    if (selectedCategories.length === 0) {
      return [];
    }

    const result: CategoryType[] = [];
    selectedCategories.forEach((selected) =>
      result.push(...fullCategoryList.filter((cat) => cat.id === selected.id)),
    );
    return result.map(({ id }) => id);
  };

  const payloadDateToDate = (date?: string | null) =>
    date ? new Date(date) : null;

  if (boardDetailData && fullCategoryList) {
    const {
      id,
      subject,
      content,
      categories: selectedCategories,
      files: { slideImages, attachments, thumbnails },
      config: {
        displayStartedAt,
        homeDisplayStartedAt,
        homeDisplayEndedAt,
        isImportant,
        commentSection,
        displaySections,
        isReserved: _isReserved,
        isSandbox,
        isListSubjectVisible,
      },
      createdAt,
      updatedAt,
      axLoungeDeploy,
    } = boardDetailData;

    const displaySectionType =
      displaySections.length === 0
        ? 'none'
        : (displaySections[0] as BoardFormData['displaySections']) ?? 'none';

    const isReserved =
      _isReserved === null ? createdAt !== displayStartedAt : _isReserved;

    const thumbnailDisplayHasSet = Boolean(
      (isReserved ? homeDisplayStartedAt : true) &&
        homeDisplayStartedAt &&
        homeDisplayEndedAt,
    );
    const thumbnailDisplayStartedAt = thumbnailDisplayHasSet
      ? payloadDateToDate(homeDisplayStartedAt)
      : null;

    const thumbnailDisplayEndAt = payloadDateToDate(homeDisplayEndedAt);

    const useThumbnailDisplayTime = Boolean(
      isTemporary
        ? thumbnailDisplayStartedAt || thumbnailDisplayEndAt
        : thumbnailDisplayStartedAt && thumbnailDisplayEndAt,
    );

    const thumbnail = thumbnails?.[0];

    // axLoungeDeploy.files가 있으면 가장 최신 파일을 찾아서 codeZipFile로 설정
    let codeZipFile: any = null;
    if (isSandbox && axLoungeDeploy?.files && axLoungeDeploy.files.length > 0) {
      // uploadedAt 기준으로 정렬해서 가장 최신 파일 찾기
      const latestFile = [...axLoungeDeploy.files].sort(
        (a, b) =>
          new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime(),
      )[0];

      // useGetFileLink로 파일 정보 가져오기
      try {
        const fileInfo = await useGetFileLink(latestFile.fileUuid);
        const remoteFile = {
          name: fileInfo.data.filename,
          link: fileInfo.data.url,
        };
        codeZipFile = {
          ...remoteFileToExtendFile(remoteFile),
          uuid: latestFile.fileUuid,
        };
      } catch (error) {
        // API 호출 실패 시 기본값 사용
        codeZipFile = {
          id: latestFile.fileUuid,
          name: `code-${latestFile.version}.zip`,
          uuid: latestFile.fileUuid,
          link: latestFile.url,
          status: 'default',
          type: 'application/zip',
          isUploaded: true,
        };
      }
    } else if (isSandbox && attachments.length > 0) {
      // axLoungeDeploy.files가 없으면 기존 로직 사용
      codeZipFile = remoteFileToExtendFile(attachments[0]);
    }

    return {
      id,
      subject: subject || '',
      content: content || '',
      categories: getCategories(selectedCategories, fullCategoryList),
      useImageSlides: slideImages.length > 0,
      imageSlides: slideImages.map(remoteFileToExtendFile),
      attachments: attachments.map(remoteFileToExtendFile),
      useUploadTime: isReserved ? BooleanOption.TRUE : BooleanOption.FALSE,
      displayTime: isReserved
        ? new Date(displayStartedAt)
        : getDefaultDisplayTime(),
      displayStartedAt: displayStartedAt ? new Date(displayStartedAt) : null,
      isImportant: isImportant ? BooleanOption.TRUE : BooleanOption.FALSE,
      commentPosition: commentSection,
      displaySections: displaySectionType,
      thumbnailFile: thumbnail ? remoteFileToExtendFile(thumbnail) : null,
      useThumbnailDisplayTime: useThumbnailDisplayTime
        ? BooleanOption.TRUE
        : BooleanOption.FALSE,
      thumbnailDisplayStartedAt,
      thumbnailDisplayEndAt,
      createdTime: new Date(createdAt),
      updatedTime: new Date(updatedAt),
      useThumbnail: thumbnail ? BooleanOption.TRUE : BooleanOption.FALSE,
      // 수정 API 는 config 를 통째로 덮어쓰므로, 받은 값을 그대로 폼에 실어 다시 보내야 한다.
      showTitleInList:
        isListSubjectVisible === false
          ? BooleanOption.FALSE
          : BooleanOption.TRUE,
      formType: (isSandbox ? 'codeUpload' : 'editor') as
        | 'editor'
        | 'codeUpload',
      codeZipFile,
      axLoungeDeploy: {
        fileUuid: codeZipFile?.uuid || '',
        version: '1.0.0',
      },
    };
  }
};
